import {
    DetailedCandidate,
    DetailedCheckResponse,
    DuplicateCheckParams,
    LeadResult,
    LlmAssessment,
    MappedLead,
} from '../store/models/DuplicateCheck';

export function isExactPlacesMatch(candidate: DetailedCandidate): boolean {
    return candidate.totalScore >= 1
        && candidate.fields.length > 0
        && candidate.fields.every((f) => f.status === 'skipped_no_input');
}

export function formatDuration(ms: number): string {
    const totalSeconds = Math.max(0, Math.round(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0 ? `${minutes} min ${seconds}s` : `${seconds}s`;
}

export function placeToLead(place: any): MappedLead {
    const companyName = place?.displayName?.text
        ?? (typeof place?.displayName === 'string' ? place.displayName : undefined)
        ?? place?.name;
    const address = place?.formattedAddress ?? place?.shortFormattedAddress;
    const phone = place?.internationalPhoneNumber ?? place?.nationalPhoneNumber;
    const website = place?.websiteUri;
    const lat = place?.location?.latitude;
    const lng = place?.location?.longitude;
    const googlePlacesId = place?.id ?? place?.placeId;

    const params: DuplicateCheckParams = {};
    if (companyName) params.companyName = String(companyName);
    if (address) params.address = String(address);
    if (phone) params.phone = String(phone);
    if (website) params.website = String(website);
    if (typeof lat === 'number') params.lat = lat;
    if (typeof lng === 'number') params.lng = lng;
    if (googlePlacesId) params.googlePlacesId = String(googlePlacesId);

    return {
        placeId: googlePlacesId ? String(googlePlacesId) : '',
        display: (companyName && String(companyName)) || (address && String(address)) || (googlePlacesId && String(googlePlacesId)) || '(ohne Name)',
        params,
    };
}

export function makeLeadResult(lead: MappedLead, data: DetailedCheckResponse): LeadResult {
    const candidates = data?.candidates ?? [];
    if (candidates.length === 0) {
        return {lead, status: 'new', bestScore: null};
    }
    const best = [...candidates].sort((a, b) => b.totalScore - a.totalScore)[0];
    return {
        lead,
        status: best.status,
        bestScore: best.totalScore,
        bestCandidateName: best.contact?.companyLine1,
        bestCandidateSap: best.contact?.sapCustomerID,
        exactPlacesMatch: isExactPlacesMatch(best),
    };
}

export function parseDetailedJson(text: string): DetailedCheckResponse {
    let parsed: any;
    try {
        parsed = JSON.parse(text);
    } catch {
        throw new Error('Ungültiges JSON');
    }
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('JSON muss ein Objekt sein');
    }
    if (!parsed.input || typeof parsed.input !== 'object' || !Array.isArray(parsed.candidates)) {
        throw new Error('Erwartet wird die Antwort des Detail-Checks mit "input" und "candidates"');
    }
    if (parsed.candidates.some((c: any) => typeof c?.contact?.stContactID !== 'string' || !c.contact.stContactID)) {
        throw new Error('Jeder Kandidat braucht eine "contact.stContactID"');
    }
    return {
        ...parsed,
        candidates: parsed.candidates.map((c: any) => ({...c, fields: Array.isArray(c.fields) ? c.fields : []})),
        totalCandidatesFound: parsed.totalCandidatesFound ?? parsed.candidates.length,
    };
}

export function splitLlmCandidates(assessments: LlmAssessment[], requestedIds: string[]): Record<string, LlmAssessment | 'missing'> {
    const byId = new Map(assessments.map((a) => [a.stContactID, a]));
    return Object.fromEntries(requestedIds.map((id) => [id, byId.get(id) ?? 'missing']));
}
